import React, {
  useMemo, useCallback, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { Col } from 'antd';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useSelector } from 'react-redux';
import ResizeObserver from 'rc-resize-observer';

import OnTraccrEmpty from '../../common/OnTraccrEmpty';

import sortByString, { getIdMap, includesTerm } from '../../helpers/helpers';

import {
  splitMultiDayShifts,
} from '../scheduleHelpers';
import ScheduleDayWithHeader from '../ScheduleDayWithHeader';
import useDivisionUsers from '../../common/hooks/useDivisionUsers';
import { USER_SCHEDULE_WEEK_HEADERS, USER_SCHEDULE_WEEKDAY_HEADERS } from '../schedule.constants';

const HEADER_HEIGHT = 25;
const BASE_WIDTH = 250;

function UserSchedule({
  day,
  startDT,
  viewType,
  shifts = [],
  onShiftSelect,
  onShiftCreate,
  onNewShiftChange,
  newShift,
  onShiftEdit,
  editShift,
  searchText,
  selectedUsers,
  onShiftDrop,
}) {
  const users = useDivisionUsers();
  const scale = useSelector((state) => state.schedule.scale);

  const scrollRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(-HEADER_HEIGHT);
  const [containerWidth, setContainerWidth] = useState(0);

  const onScroll = useCallback((e) => {
    const {
      target: {
        scrollTop: targetTop,
      } = {},
    } = e;
    setScrollTop(targetTop - HEADER_HEIGHT);
  }, []);

  const userMap = useMemo(() => getIdMap(users), [users]);

  const sortedUsers = useMemo(() => {
    const sUsers = users.filter((user) => user.active);
    sUsers.sort(sortByString('name'));
    return sUsers;
  }, [users]);

  const {
    multiDay: allDayShifts,
    interDay: interDayShifts,
    numberOfColumns,
  } = useMemo(() => {
    let daysToIterate = 1;
    let relevantDay = day;

    if (viewType === 'UserWeek') {
      daysToIterate = 7;
      relevantDay = startDT;
    }
    if (viewType === 'UserWeekdays') {
      daysToIterate = 5;
      relevantDay = startDT;
    }

    return {
      ...splitMultiDayShifts(Object.values(shifts).flat(), relevantDay, daysToIterate),
      numberOfColumns: daysToIterate,
    };
  }, [shifts, day, viewType, startDT]);

  const shiftsByUser = useMemo(() => {
    const selectedUserMap = new Set(selectedUsers || []);

    const shiftUserMap = {};
    const addToMap = (userShifts, key) => {
      userShifts.forEach((shiftDay, index) => {
        shiftDay.forEach((shift) => {
          const {
            users: shiftUsers = [],
          } = shift;
          if (shiftUsers.length === 0) return;
          shiftUsers.forEach((userId) => {
            if (!(userId in shiftUserMap)) {
              const {
                [userId]: {
                  name: userName,
                } = {},
              } = userMap;
              shiftUserMap[userId] = {
                userId,
                name: userName,
                shifts: [],
                allDayShifts: [],
              };
            }
            if (!shiftUserMap[userId][key][index]) {
              shiftUserMap[userId][key][index] = [];
            }
            shiftUserMap[userId][key][index].push(shift);
          });
        });
      });
    };
    addToMap(allDayShifts, 'allDayShifts');
    addToMap(interDayShifts, 'shifts');

    const finalShifts = [];
    sortedUsers.forEach((user) => {
      if (selectedUserMap.size && !selectedUserMap.has(user.id)) return;

      if (user.id in shiftUserMap) {
        finalShifts.push(shiftUserMap[user.id]);
      } else if (!searchText || includesTerm(user.name, searchText)) {
        finalShifts.push({
          userId: user.id,
          name: user.name,
          shifts: [],
          allDayShifts: [],
        });
      }
    });
    return finalShifts;
  }, [
    allDayShifts,
    interDayShifts,
    userMap,
    searchText,
    sortedUsers,
    selectedUsers,
  ]);

  const styleByIdx = (idx) => {
    const style = { flex: 1 };
    if (idx > 0) style.marginLeft = 10;
    if (idx < shiftsByUser.length - 1) style.marginRight = 10;
    return style;
  };

  const userColumnWidth = useMemo(() => {
    const numOfUsers = shiftsByUser?.length;

    const totalUserColumnWidth = (
      // Start and end only have margin 10
      ((BASE_WIDTH + 20) * (numOfUsers < 2 ? 0 : numOfUsers - 2))
      // First and last user have margin 10
      + ((BASE_WIDTH + 10) * (numOfUsers > 2 ? 2 : numOfUsers))
    ) * scale;

    if (totalUserColumnWidth > containerWidth) return BASE_WIDTH;

    let baseContainerWidth = containerWidth / scale;
    baseContainerWidth -= (20 * (numOfUsers < 2 ? 0 : numOfUsers - 2));
    baseContainerWidth -= (10 * (numOfUsers > 2 ? 2 : numOfUsers));
    baseContainerWidth -= 50; // Additional Padding

    return Math.floor(baseContainerWidth / numOfUsers);
  }, [containerWidth, shiftsByUser, scale]);

  const getScheduleColumns = useCallback(({
    userId,
    idx,
    userShifts,
    userAllDay,
  }) => {
    const base = (
      <ScheduleDayWithHeader
        key="daily"
        userId={userId}
        day={day}
        userShifts={userShifts?.[0]}
        userAllDay={userAllDay?.[0]}
        onShiftCreate={onShiftCreate}
        newShift={newShift}
        onNewShiftChange={onNewShiftChange}
        onShiftSelect={onShiftSelect}
        editShift={editShift}
        onShiftEdit={onShiftEdit}
        scrollTop={scrollTop}
        onShiftDrop={onShiftDrop}
        viewType={viewType}
      />
    );

    if (viewType === 'UserWeekdays' || viewType === 'UserWeek') {
      const headers = viewType === 'UserWeekdays' ? USER_SCHEDULE_WEEKDAY_HEADERS : USER_SCHEDULE_WEEK_HEADERS;

      return (
        <div
          className="schedule-user-column-week"
          style={{
            ...styleByIdx(idx),
            width: `${userColumnWidth * scale}px`,
            minWidth: `${userColumnWidth * scale}px`,
          }}
        >
          {headers.map((_, index) => {
            const weekday = startDT.plus({ days: index });
            return (
              <div
                key={weekday.toMillis()}
                className="schedule-user-column-week-day"
                style={{ width: (userColumnWidth * scale) / numberOfColumns }}
              >
                <div className="schedule-user-column-week-header">{headers[index]}</div>
                <Col>
                  {React.cloneElement(base, {
                    key: weekday.toMillis(),
                    day: weekday,
                    hideHours: index !== 0,
                    hideGrid: index !== 0,
                    userShifts: userShifts?.[index] || [],
                    userAllDay: userAllDay?.[index] || [],
                    shiftBlockStyle: {
                      width: ((userColumnWidth * scale) / numberOfColumns) - 5,
                      maxWidth: ((userColumnWidth * scale) / numberOfColumns)- 5,
                      left: 0,
                    },
                    headerStyle: {
                      width: (userColumnWidth * scale) / numberOfColumns,
                      marginLeft: 0,
                    },
                    hideShiftText: scale <= (viewType === 'UserWeek' ? 0.75 : 0.5),
                  })}
                </Col>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <Col
        key={userId}
        style={{
          ...styleByIdx(idx),
          width: `${userColumnWidth * scale}px`,
          minWidth: `${userColumnWidth * scale}px`,
        }}
        className="schedule-user-column"
      >
        {base}
      </Col>
    );
  }, [
    day,
    onShiftCreate,
    onNewShiftChange,
    newShift,
    onShiftSelect,
    editShift,
    onShiftEdit,
    scrollTop,
    onShiftDrop,
    viewType,
    scale,
    numberOfColumns,
    userColumnWidth,
  ]);

  const onResize = useCallback(({ width }) => {
    setContainerWidth(width);
  }, []);

  return (
    <DndProvider backend={HTML5Backend}>
      <ResizeObserver onResize={onResize}>
        <div className="schedule-user-container">
          <div className="schedule-user-name-header">
            {
              shiftsByUser.length > 0 && shiftsByUser.map((userShiftObj, idx) => (
                <div
                  className="schedule-user-column-header"
                  key={userShiftObj.userId}
                  style={{
                    ...styleByIdx(idx),
                    width: `${(userColumnWidth * scale)}px`,
                    minWidth: `${(userColumnWidth * scale)}px`,
                  }}
                >
                  {userShiftObj.name}
                </div>
              ))
            }
          </div>
          <div
            className="schedule-user-body"
            onScroll={onScroll}
            ref={scrollRef}
          >
            {
              shiftsByUser?.map((userShiftObj, idx) => {
                const {
                  userId,
                  shifts: userShifts = [],
                  allDayShifts: userAllDay = [],
                } = userShiftObj;
                return getScheduleColumns({
                  userId,
                  idx,
                  userShifts,
                  userAllDay,
                })
              })
            }
          </div>
          {
            shiftsByUser.length === 0 && <OnTraccrEmpty title="User Schedule" />
          }
        </div>
      </ResizeObserver>
    </DndProvider>
  );
}

UserSchedule.propTypes = {
  day: PropTypes.instanceOf(DateTime).isRequired,
  shifts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
  })).isRequired,
  onShiftSelect: PropTypes.func.isRequired,
  onShiftCreate: PropTypes.func.isRequired,
  onShiftEdit: PropTypes.func.isRequired,
  onNewShiftChange: PropTypes.func.isRequired,
  newShift: PropTypes.objectOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
  })).isRequired,
  editShift: PropTypes.objectOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
  })).isRequired,
  searchText: PropTypes.string.isRequired,
  onShiftDrop: PropTypes.func.isRequired,
};

export default UserSchedule;
